<template>
  <div class="auction-preview">
    <div v-if="!data || data.length === 0" class="no-data">
      <p>No auction data detected in the uploaded file</p>
    </div>
    
    <div v-else class="data-table-container">
      <table class="data-table">
        <thead>
          <tr>
            <th>Lot No</th>
            <th>Line ID</th>
            <th>Sale Center</th>
            <th>Sale Price (¢/kg)</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(auction, index) in data" :key="index">
            <td>
              <input 
                type="text" 
                v-model="auction.LotNo" 
                class="table-input"
                :class="{ 'has-error': validationErrors[index]?.LotNo }"
              >
              <div v-if="validationErrors[index]?.LotNo" class="error-message">
                {{ validationErrors[index].LotNo }}
              </div>
            </td>
            <td>
              <input 
                type="text" 
                v-model="auction.LineID" 
                class="table-input"
                :class="{ 'has-error': validationErrors[index]?.LineID }"
              >
              <div v-if="validationErrors[index]?.LineID" class="error-message">
                {{ validationErrors[index].LineID }}
              </div>
            </td>
            <td>
              <input 
                type="text" 
                v-model="auction.SaleCenter" 
                class="table-input"
              >
            </td>
            <td>
              <input 
                type="number" 
                v-model.number="auction.SalePrice_cents_per_kg_greasy" 
                class="table-input"
              >
            </td>
            <td class="actions-cell">
              <button class="btn-icon" @click="expandAuction(index)" :title="auction.expanded ? 'Collapse' : 'Expand'">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <polyline v-if="!auction.expanded" points="6 9 12 15 18 9"></polyline>
                  <polyline v-else points="18 15 12 9 6 15"></polyline>
                </svg>
              </button>
              <button class="btn-icon delete" @click="removeAuction(index)" title="Remove">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M3 6h18"></path>
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                </svg>
              </button>
            </td>
          </tr>
        </tbody>
      </table>
      
      <!-- Expanded Auction Details -->
      <div v-for="(auction, index) in data" :key="`details-${index}`" class="expanded-details" v-show="auction.expanded">
        <div class="details-grid">
          <div class="detail-group">
            <label>Sale Date/Time</label>
            <input type="datetime-local" v-model="auction.SaleDateTime" class="detail-input">
          </div>
          
          <div class="detail-group">
            <label>Broker Code</label>
            <input type="text" v-model="auction.BrokerCode" class="detail-input">
          </div>
        </div>
      </div>
    </div>
    
    <div class="add-item-row">
      <button class="btn-add" @click="addNewAuction">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <line x1="12" y1="5" x2="12" y2="19"></line>
          <line x1="5" y1="12" x2="19" y2="12"></line>
        </svg>
        Add Auction
      </button>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    data: {
      type: Array,
      default: () => []
    }
  },
  
  data() {
    return {
      validationErrors: [],
      auctionTemplate: {
        LotNo: '',
        LineID: '',
        SaleCenter: 'Sydney Wool Auction Center',
        SaleDateTime: new Date().toISOString().slice(0, 16),
        SalePrice_cents_per_kg_greasy: 0,
        BrokerCode: '',
        expanded: false
      }
    };
  },
  
  watch: {
    data: {
      handler(newData) {
        // Initialize expanded property if not present
        if (newData) {
          newData.forEach(auction => {
            if (auction.expanded === undefined) {
              this.$set(auction, 'expanded', false);
            }
          });
        }
        
        // Reset validation errors when data changes
        this.validationErrors = Array(newData?.length || 0).fill(null);
        
        // Emit updated data
        this.$emit('update', newData);
      },
      deep: true,
      immediate: true
    }
  },
  
  methods: {
    expandAuction(index) {
      if (this.data[index]) {
        this.$set(this.data[index], 'expanded', !this.data[index].expanded);
      }
    },
    
    removeAuction(index) {
      if (confirm('Are you sure you want to remove this auction?')) {
        const updatedData = [...this.data];
        updatedData.splice(index, 1);
        this.$emit('update', updatedData);
      }
    },
    
    addNewAuction() {
      const updatedData = [...this.data];
      updatedData.push({...this.auctionTemplate});
      this.$emit('update', updatedData);
    },
    
    validateAuctions() {
      let isValid = true;
      const errors = Array(this.data.length).fill(null).map(() => ({}));
      
      this.data.forEach((auction, index) => {
        // Validate LotNo
        if (!auction.LotNo) {
          errors[index].LotNo = 'Lot No is required';
          isValid = false;
        }
        
        // Validate LineID
        if (!auction.LineID) {
          errors[index].LineID = 'Line ID is required';
          isValid = false;
        }
      });
      
      this.validationErrors = errors;
      return isValid;
    }
  }
};
</script>

<style scoped>
.auction-preview {
  margin-bottom: 24px;
}

.no-data {
  padding: 40px;
  text-align: center;
  background-color: #f9fafb;
  border-radius: 6px;
  color: #6b7280;
}

.data-table-container {
  overflow-x: auto;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.data-table th {
  background-color: #f9fafb;
  padding: 12px 16px;
  text-align: left;
  font-weight: 600;
  color: #374151;
  border-bottom: 1px solid #e5e7eb;
}

.data-table td {
  padding: 12px 16px;
  border-bottom: 1px solid #e5e7eb;
  vertical-align: top;
}

.table-input {
  width: 100%;
  padding: 8px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 14px;
}

.table-input:focus {
  border-color: #3A895F;
  outline: none;
  box-shadow: 0 0 0 2px rgba(58, 137, 95, 0.2);
}

.table-input.has-error {
  border-color: #ef4444;
}

.error-message {
  color: #ef4444;
  font-size: 12px;
  margin-top: 4px;
}

.actions-cell {
  white-space: nowrap;
  width: 80px;
}

.btn-icon {
  background: none;
  border: none;
  color: #6b7280;
  cursor: pointer;
  padding: 6px;
  border-radius: 4px;
  margin-right: 4px;
}

.btn-icon:hover {
  background-color: #f3f4f6;
  color: #111827;
}

.btn-icon.delete:hover {
  background-color: #fee2e2;
  color: #b91c1c;
}

.expanded-details {
  background-color: #f9fafb;
  padding: 16px;
  margin-bottom: 16px;
  border-radius: 0 0 6px 6px;
  border: 1px solid #e5e7eb;
  border-top: none;
}

.details-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 16px;
}

.detail-group {
  display: flex;
  flex-direction: column;
}

.detail-group label {
  font-size: 12px;
  font-weight: 500;
  color: #6b7280;
  margin-bottom: 4px;
}

.detail-input {
  padding: 8px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 14px;
}

.add-item-row {
  margin-top: 16px;
  display: flex;
  justify-content: flex-start;
}

.btn-add {
  display: flex;
  align-items: center;
  gap: 6px;
  background-color: white;
  border: 1px dashed #d1d5db;
  color: #3A895F;
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-add:hover {
  background-color: #f9fafb;
  border-color: #3A895F;
}
</style>
